import { useFeatureGate, useStatsigClient } from '@statsig/expo-bindings';

import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { getLiveSplitSourceMoneyMapId } from '../utils/moneyMapMemberEditEligibility';
import { useMembershipMoneyMaps } from './useMembershipMoneyMaps';
import { useRoadmapFeature } from './useRoadmapFeature';

/**
 * Tab body (label is "Money System" for both money_map and money_system):
 * goals | money_map | money_system | loading.
 * money_system when FF is on and eligibleForMemberEdit latches; banking/home
 * only treat money_system as the new experience.
 */
export type RoadmapTabExperience =
    | 'goals'
    | 'money_map'
    | 'money_system'
    | 'loading';

export const useRoadmapTabExperience = (): RoadmapTabExperience => {
    const { disableRoadmapFeatures } = useRoadmapFeature();
    const { isLoading: isStatsigLoading } = useStatsigClient();
    const { value: enableMoneySystemTab } = useFeatureGate(
        FeatureFlags.ENABLE_MONEY_SYSTEM_TAB,
    );
    const {
        previewAndActiveMaps,
        liveSplitMoneyMap,
        currentIncomeSplit,
        isLoading,
    } = useMembershipMoneyMaps();

    if (disableRoadmapFeatures) {
        return 'goals';
    }

    // Avoid flashing money_map while Statsig/gates default false.
    if (isStatsigLoading || isLoading) {
        return 'loading';
    }

    if (!enableMoneySystemTab) {
        return 'money_map';
    }

    const liveSplitSourceMoneyMapId =
        getLiveSplitSourceMoneyMapId(currentIncomeSplit);

    // Live-split source may be INACTIVE; latch that map, not only PREVIEW/ACTIVE.
    if (liveSplitSourceMoneyMapId != null) {
        return liveSplitMoneyMap?.eligibleForMemberEdit === true
            ? 'money_system'
            : 'money_map';
    }

    // No live split yet (e.g. early Go) — PREVIEW/ACTIVE latch.
    if (previewAndActiveMaps.length === 0) {
        return 'money_system';
    }

    return previewAndActiveMaps.every(
        (map) => map.eligibleForMemberEdit === true,
    )
        ? 'money_system'
        : 'money_map';
};
